/**
 * Code-defined app-surface configuration.
 *
 * Onboarding a new consumer app (IT portal, external SDK embed, …) is a PR
 * that adds an `AppSurface` enum value in `values.ts` plus an entry here —
 * no DB migration, no admin UI, no request-time registration. Mirrors the
 * capability-config pattern in `@workspace/ai`.
 *
 * Lives alongside `values.ts` (dependency-free constants) so it can be
 * imported from every layer that needs it without inverting a package
 * dependency:
 *   - `@workspace/auth` (token exchange — validates client-id allowlists),
 *   - `@workspace/ai` (capability gating — `enabledCapabilities`),
 *   - `apps/web/proxy.ts` (CORS — `allowedOrigins`).
 *
 * The module is client/edge-safe: no Node-only imports, and every
 * environment read is lazy (a function, not a module-load constant) so
 * Key Vault boot-time hydration in `instrumentation.ts` is observed.
 */

import { type AppSurface, SURFACE_LABELS } from "./values";

/**
 * Name of the first-party Office-add-in session cookie, and the single source
 * of truth for the literal. Lives here (dependency-free, edge/workflow-safe)
 * so the Entra-exchange policy that *sets* the cookie owns the name. The
 * embedded-host readers re-export it via `@workspace/copilots`
 * (`packages/features/copilots/src/types.ts`), and
 * `apps/web/lib/office-addin-session.ts` re-exports it from there — so the
 * writer and readers can never drift.
 */
export const ADDIN_SESSION_COOKIE_NAME = "magnus-addin-session-token";

/**
 * Per-surface policy for the Entra → Magnus token exchange. Absence of
 * this policy on a surface means that surface cannot mint a session via
 * the exchange endpoint at all (web/mobile use Better Auth SSO; Procore
 * has its own session route).
 *
 * The shared exchange pipeline (`@workspace/auth` core + the app-layer
 * handler) consumes these fields directly — there is no route-identity
 * branching. Tightening a surface's posture is a one-line edit here.
 */
export interface EntraExchangePolicy {
  /**
   * Entra application (client) IDs permitted to mint a Magnus session for
   * this surface, matched against the token's `azp`/`appid`. Read lazily so
   * env hydration order doesn't matter.
   */
  allowedEntraClientIds: () => string[];
  /**
   * Office NAA hands back opaque Microsoft Graph tokens (not readable JWTs),
   * so the add-in surfaces must allow them — authenticity is proven by the
   * Graph `/me` call. External surfaces require readable JWTs and reject
   * opaque tokens.
   */
  allowOpaqueTokens: boolean;
  /**
   * What to do when `allowedEntraClientIds()` is empty. Office keeps today's
   * behavior (`warn`: allow + log loudly in production); external surfaces
   * hard-fail (`reject`) so an unconfigured surface can't mint sessions for
   * any consented Entra app (confused-deputy, ADR-044 §7).
   */
  emptyAllowlistBehavior: "warn" | "reject";
  /**
   * When true, the app-layer handler requires the resolved user to hold the
   * Magnus `access` role (`hasAppAccess`) before minting. Office preserves
   * its current behavior (false — access is enforced downstream).
   */
  requireAppAccess: boolean;
  /**
   * When set, the exchange response also sets this first-party cookie (Office
   * add-in path). Omit for bearer-only cross-origin surfaces.
   */
  sessionCookieName?: string;
}

export interface AppSurfaceConfig {
  /**
   * Opt-in for the client-passed `surfacePromptAppendix` on the chat body
   * (plan 032 §B2). When true, a bounded, sanitized, subordinate instruction
   * appendix supplied by the host application (via the SDK) is emitted inside
   * `<surface_instructions>`. Surfaces that leave this unset silently drop
   * the field — the server never trusts it, and the flag only controls
   * whether it is rendered at all.
   */
  allowClientSurfacePrompt?: boolean;
  /**
   * Whether `clientTool()` declarations compose for this surface. Existing
   * surfaces allow them (Office add-ins depend on it); a locked-down surface
   * can set this false to drop all client tools.
   */
  allowClientTools: boolean;
  /**
   * Cross-origin browser origins allowed to call the SDK-facing API routes
   * for this surface (drives CORS). Read lazily. Empty for same-origin
   * surfaces (Office add-ins, web, mobile).
   */
  allowedOrigins: () => string[];
  /**
   * Opt-in for host-declared client tools on the chat body (ADR-096). When
   * true, a bounded set of `{ name, description, inputSchema }` declarations
   * supplied by the host application is admitted into this turn's tool set and
   * dispatched over the ADR-043 client protocol. UNTRUSTED, like
   * `allowClientSurfacePrompt`. Surfaces that leave this unset silently drop
   * the field. Requires `allowClientTools: true`.
   *
   * Deliberately NOT folded into `allowClientTools`: every surface in this
   * file sets that one true, so reusing it would enable runtime tool
   * declaration on the flagship web app the moment the field shipped.
   */
  allowHostDeclaredTools?: boolean;
  displayName: string;
  /**
   * Capability allowlist for this surface. `undefined` = no surface-level
   * restriction (existing behavior — capability-side `surfaces` gating still
   * applies). When set, only listed capability ids compose on this surface.
   *
   * Typed `readonly string[]` rather than `CapabilityId[]`: that type lives
   * in `@workspace/ai`, which depends on this package, so importing it here
   * would invert the dependency. A type-level assertion in `@workspace/ai`
   * (see `capabilities/compose.ts`) pins these strings to valid capability
   * ids at compile time.
   */
  enabledCapabilities?: readonly string[];
  /** Absent = this surface cannot mint sessions via Entra exchange. */
  entraExchange?: EntraExchangePolicy;
  id: AppSurface;
  /**
   * When true, this surface may not call the `/api/sdk/v1` REST data plane
   * at all — the SDK auth guard (plan 029) answers 403. v1 surface
   * enforcement is exactly this one switch; per-resource ACLs are the
   * follow-on and read the same resolved surface.
   */
  sdkRestDisabled?: boolean;
  /**
   * Static, code-reviewed instruction block appended to the system prompt
   * when a chat runs on this surface (emitted as `<surface_instructions>`,
   * plan 032 §B1). Identity/safety blocks precede it and take precedence.
   * Keep cache-stable — no per-request values; those belong in
   * `surfaceContext` on the un-cached message tail.
   */
  surfacePrompt?: string;
}

/** Parse a comma-separated env var into a trimmed, non-empty string list. */
function parseCsvEnv(name: string): string[] {
  return (process.env[name] ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Shared policy for all three Office add-in surfaces. They authenticate
 * identically (MSAL NAA → opaque Graph token → first-party cookie), so they
 * share one instance rather than duplicating the fields three times.
 *
 * Behavior is intentionally identical to the pre-refactor
 * `office-addin-exchange` module: client-id allowlist from
 * `MICROSOFT_ALLOWED_CLIENT_IDS`, opaque tokens allowed, warn (not reject) on
 * an empty allowlist, no `hasAppAccess` gate at exchange time, first-party
 * cookie set on the response.
 */
const officeAddinExchangePolicy: EntraExchangePolicy = {
  allowedEntraClientIds: () => parseCsvEnv("MICROSOFT_ALLOWED_CLIENT_IDS"),
  allowOpaqueTokens: true,
  emptyAllowlistBehavior: "warn",
  requireAppAccess: false,
  sessionCookieName: ADDIN_SESSION_COOKIE_NAME,
};

const officeAddinConfig = (id: AppSurface): AppSurfaceConfig => ({
  id,
  displayName: SURFACE_LABELS[id as keyof typeof SURFACE_LABELS] ?? id,
  // Office task panes load this same web app (same origin); no cross-origin
  // CORS entry needed.
  allowedOrigins: () => [],
  allowClientTools: true,
  entraExchange: officeAddinExchangePolicy,
});

export const appSurfaceConfigs = {
  "standalone-web": {
    id: "standalone-web",
    displayName: SURFACE_LABELS["standalone-web"],
    allowedOrigins: () => [],
    allowClientTools: true,
  },
  "standalone-mobile": {
    id: "standalone-mobile",
    displayName: SURFACE_LABELS["standalone-mobile"],
    allowedOrigins: () => [],
    allowClientTools: true,
  },
  "excel-add-in": officeAddinConfig("excel-add-in"),
  "outlook-add-in": officeAddinConfig("outlook-add-in"),
  "word-add-in": officeAddinConfig("word-add-in"),
  embedded: {
    id: "embedded",
    displayName: SURFACE_LABELS.embedded,
    allowedOrigins: () => parseCsvEnv("EMBEDDED_ALLOWED_ORIGINS"),
    allowClientTools: true,
    entraExchange: {
      allowedEntraClientIds: () =>
        parseCsvEnv("EMBEDDED_ALLOWED_ENTRA_CLIENT_IDS"),
      allowOpaqueTokens: false,
      emptyAllowlistBehavior: "reject",
      requireAppAccess: true,
    },
  },
  "procore-copilot": {
    id: "procore-copilot",
    displayName: SURFACE_LABELS["procore-copilot"],
    // Procore runs cross-site but authenticates through its own session
    // route, not the Entra exchange; no SDK CORS entry in Phase 1.
    allowedOrigins: () => [],
    allowClientTools: true,
  },
  "freshservice-copilot": {
    id: "freshservice-copilot",
    displayName: SURFACE_LABELS["freshservice-copilot"],
    // The Freshservice FDK app iframes this web app, so browser calls are
    // same-origin; auth runs through `/api/auth/freshservice-session`, not
    // the Entra exchange.
    allowedOrigins: () => [],
    allowClientTools: true,
  },
  appcopilot: {
    id: "appcopilot",
    displayName: SURFACE_LABELS.appcopilot,
    // BFF/YARP calls Magnus server-to-server; browser CORS only if their
    // SPA ever calls Magnus directly (lazy env, empty until needed).
    allowedOrigins: () => parseCsvEnv("APPCOPILOT_ALLOWED_ORIGINS"),
    // The AppCopilot SPA registers `defineClientTool` executors for its own
    // route/navigation/clipboard bridge and declares them per turn as
    // host-declared client tools (ADR-096) — no server-side capability.
    allowClientTools: true,
    // Opt-in for host-declared client tools (ADR-096, plan 038). AppCopilot
    // is the first — and so far only — surface that declares its own tools
    // on the chat body via the SDK's `host_` name prefix.
    allowHostDeclaredTools: true,
    // no entraExchange — this surface authenticates via the issuer registry
    // Owner decision (plan 032 §B2, 2026-08-09): the AppCopilot prompt is
    // supplied by the client via the SDK's `surfacePromptAppendix`, not baked
    // in here. No server-registered `surfacePrompt` for this surface.
    allowClientSurfacePrompt: true,
  },
  create: {
    id: "create",
    displayName: SURFACE_LABELS.create,
    // Magnus Create runs inside this same web app at `/create` (a
    // standalone-web host with a distinct surface id for capability gating
    // and chat scoping); same origin, no exchange policy.
    allowedOrigins: () => [],
    allowClientTools: true,
  },
} satisfies Record<AppSurface, AppSurfaceConfig>;

/** Look up the config for a surface. */
export function getSurfaceConfig(surface: AppSurface): AppSurfaceConfig {
  return appSurfaceConfigs[surface];
}

/**
 * Loopback hosts only: `localhost`, `127.0.0.1`, `[::1]`. Used so a surface
 * that lists any `http://localhost:<port>` (or `https://`) also admits other
 * loopback ports of the same scheme — Vite vs the .NET BFF vs whatever the
 * next local tool binds. Non-loopback origins stay exact-match.
 */
function loopbackScheme(origin: string): "http:" | "https:" | null {
  try {
    const url = new URL(origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    const host = url.hostname;
    if (
      host !== "localhost" &&
      host !== "127.0.0.1" &&
      host !== "::1" &&
      host !== "[::1]"
    ) {
      return null;
    }
    return url.protocol;
  } catch {
    return null;
  }
}

function originMatchesAllowlistEntry(origin: string, allowed: string): boolean {
  if (allowed === origin) {
    return true;
  }
  const originScheme = loopbackScheme(origin);
  return originScheme !== null && originScheme === loopbackScheme(allowed);
}

/**
 * True when `origin` is allowed by at least one surface. Drives the CORS
 * decision — the same physical Magnus origin serves every surface, so the
 * request is admitted if any configured surface trusts the caller's origin.
 * Case-sensitive exact match (browsers send origins lowercased and without a
 * trailing slash), except loopback: any listed `http://localhost:<port>`
 * (or `https://`, or `127.0.0.1` / `[::1]`) admits every loopback origin of
 * the same scheme. The ACAO header still reflects the request Origin.
 */
export function isOriginAllowedForAnySurface(origin: string): boolean {
  if (!origin) {
    return false;
  }
  for (const config of Object.values(appSurfaceConfigs)) {
    if (
      config
        .allowedOrigins()
        .some((allowed) => originMatchesAllowlistEntry(origin, allowed))
    ) {
      return true;
    }
  }
  return false;
}
